<template lang="html">
  <div :style="`height:${windowHeight}px;width:100%`">
    <v-btn
      v-if="params('draft') === 'true'"
      title="Copier la position actuelle"
      absolute
      small
      style="top:180px;right:10px;z-index:10;padding:0;min-width:29px"
      @click="bboxPasted()"
    >
      <v-icon>mdi-crop-free</v-icon>
    </v-btn>
    <v-btn
      v-if="config.satelliteTiles"
      :title="satellite ? 'Vue plan' : 'Vue satellite'"
      absolute
      small
      style="top:144px;right:10px;z-index:10;padding:0;min-width:29px"
      @click="toggleSatellite()"
    >
      <v-icon v-if="satellite">
        mdi-layers
      </v-icon>
      <v-icon v-else>
        mdi-satellite
      </v-icon>
    </v-btn>
    <v-progress-linear
      v-if="loading"
      style="position:absolute;z-index:1;max-width:100%;pointer-events: none;"
      indeterminate
      color="primary"
    />
    <div
      id="map"
      style="height:100%"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Popup from './Popup.vue'
import Vue from 'vue'
import eventBus from '../assets/event-bus.js'
import colors from '../assets/colors'
import axios from 'axios'
import { bbox2zoom } from '@data-fair/components-app/map/utils.js'
import 'maplibre-gl/dist/maplibre-gl.css'
const maplibregl = require('maplibre-gl')
const CPopup = Vue.extend(Popup)

const zoomLevel = {
  housenumber: 17,
  street: 16,
  municipality: 12
}

const satelliteTilesPositions = {
  'klokantech-basic': 'admin_sub',
  admindiv: 'admin_country',
  'dark-matter': 'boundary_state',
  'fiord-color ': 'boundary_state',
  'osm-bright': 'boundary-land-level-4',
  positron: 'boundary_state',
  'osm-liberty': 'boundary_3',
  cassini: 'place-hamlet_small',
  cadastre: 'admin_sub',
  'plan-ign-standard': 'réseau hydro - cours d\'eau large '
}

const styles = {
  'plan-ign-standard': 'https://data.geopf.fr/annexes/ressources/vectorTiles/styles/PLAN.IGN/standard.json'
}

export default {
  data: () => ({
    dataTilesUrl: null,
    satellite: false,
    query: null
  }),
  computed: {
    ...mapState(['dataset', 'loading', 'quantiles', 'colorMetrics']),
    ...mapGetters(['datasetUrl', 'tileserverUrl', 'config', 'radiusMetrics']),
    idProperty () {
      // _id should always be present, except for maybe some older datasets
      return this.dataset && this.dataset.schema.find(p => p.key === '_id') ? '_id' : '_i'
    }
  },
  watch: {
    async windowHeight () {
      if (this.map) {
        await this.$nextTick()
        this.map.resize()
      }
    }
  },
  mounted () {
    this.satellite = this.config.satelliteDefault
    eventBus.$on('query', async (query) => {
      this.query = query
      var select = [this.idProperty]
      if (this.config.colors && this.config.colors.type === 'enum' && this.config.colors.enumOptions.field) select.push(this.config.colors.enumOptions.field)
      else if (this.config.colors && this.config.colors.type === 'range' && this.config.colors.rangeOptions.field) select.push(this.config.colors.rangeOptions.field)
      if (this.config.radius && this.config.radius.field) select.push(this.config.radius.field)
      this.dataTilesUrl = `${this.datasetUrl}/lines?format=pbf&size=10000&sort=_rand&select=${select.join(',') + query}&xyz={x},{y},{z}`
      if (!this.map) {
        await this.initMap()
        this.map.on('sourcedata', (e) => {
          this.$store.commit('setAny', { loading: !this.map.areTilesLoaded() })
          if (this.map.areTilesLoaded()) {
            // turn off sourcedata listener if its no longer needed
            this.map.off('sourcedata')
          }
        })

        // "idle" event seems to be the best indication that the map is fully loaded
        this.map.on('idle', () => {
          if (window.triggerCapture && this.map.getSource('data-fair')) window.triggerCapture()
        })
      } else {
        if (this.map.isStyleLoaded()) {
          if (this.map.getSource('data-fair')) {
            this.map.removeLayer('dataset-points')
            this.map.removeSource('data-fair')
          }
          this.map.addSource('data-fair', { type: 'vector', tiles: [this.dataTilesUrl] })
          this.updatePointsLayer()
          if (this.config.tooltip && this.config.tooltip.length) {
            this.map.on('mouseenter', 'dataset-points', (e) => {
              this.map.getCanvas().style.cursor = 'pointer'
            })
            this.map.on('mouseleave', 'dataset-points', () => {
              this.map.getCanvas().style.cursor = ''
            })
          }
        }
        if (this.config.useDataBbox) {
          const { bbox } = (await axios.get(`${this.datasetUrl}/lines?size=0&format=geojson` + this.query)).data
          this.map.fitBounds(maplibregl.LngLatBounds.convert(bbox), { maxZoom: 15 })
        }
      }
    })
    eventBus.$on('goTo', pos => {
      this.map.setZoom(zoomLevel[pos[2]] || 11)
      this.map.setCenter([pos[1], pos[0]])
    })
    eventBus.$on('resetPosition', () => {
      if (!this.map) return
      const bbox = (this.config.bbox && this.config.bbox.length) ? JSON.parse(this.config.bbox) : this.dataset.bbox
      const initialBounds = maplibregl.LngLatBounds.convert(bbox)
      this.map.fitBounds(initialBounds)
    })
  },
  methods: {
    async initMap () {
      if (!maplibregl) return
      let bbox = (this.config.bbox && this.config.bbox.length) ? JSON.parse(this.config.bbox) : this.dataset.bbox
      if (this.config.useDataBbox) {
        bbox = (await axios.get(`${this.datasetUrl}/lines?size=0&format=geojson` + this.query)).data.bbox
      }
      const initialBounds = maplibregl.LngLatBounds.convert(bbox)
      const params = new URLSearchParams(window.location.search)
      const zoom = params.get('zoom') ? Number(params.get('zoom')) : (initialBounds ? bbox2zoom(bbox) : 7)
      const center = params.get('lng') && params.get('lat') ? [Number(params.get('lng')), Number(params.get('lat'))] : (initialBounds ? initialBounds.getCenter().toArray() : [2.0833, 47.2167])
      this.map = new maplibregl.Map({
        container: 'map',
        center,
        zoom,
        style: styles[this.config.style] || (this.tileserverUrl + '/styles/' + (this.config.style || 'klokantech-basic') + '/style.json'),
        transformRequest: (url, resourceType) => {
          const ret = { url }
          if (url.includes('/data-fair')) ret.credentials = 'include' // include cookies, for data-fair sessions
          return ret
        },
        attributionControl: false
      }).addControl(new maplibregl.AttributionControl({
        compact: false
      }))
      this.map.addControl(new maplibregl.NavigationControl(), 'top-right')
      this.map.addControl(new maplibregl.ScaleControl(), 'bottom-left')
      if (this.config.geolocate) this.initGeolocation()

      // Disable map rotation using right click + drag
      this.map.dragRotate.disable()
      // Disable map rotation using touch rotation gesture
      this.map.touchZoomRotate.disableRotation()

      this.map.on('click', (e) => this.showTooltip([e.lngLat.lng, e.lngLat.lat]))
      this.map.on('moveend', (e) => {
        const params = new URLSearchParams(window.location.search)
        params.set('zoom', e.target.getZoom())
        const { lat, lng } = e.target.getCenter()
        params.set('lng', lng)
        params.set('lat', lat)
        window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
      })

      // Finalize map after first load
      this.map.once('load', () => {
        this.map.addSource('data-fair', { type: 'vector', tiles: [this.dataTilesUrl] })
        this.updatePointsLayer()

        if (this.config.satelliteTiles) {
          this.map.addSource('satellite', {
            type: 'raster',
            tiles: [
              'https://data.geopf.fr/tms/1.0.0/ORTHOIMAGERY.ORTHOPHOTOS/{z}/{x}/{y}.jpeg'
            ]
          })
          this.map.addLayer({
            id: 'satellite-tiles',
            type: 'raster',
            source: 'satellite'
          }, satelliteTilesPositions[this.config.style])
          if (!this.config.satelliteDefault) { this.map.setLayoutProperty('satellite-tiles', 'visibility', 'none') }
        }

        if (this.config.tooltip && this.config.tooltip.length) {
          setTimeout(() => {
            const params = new URLSearchParams(window.location.search)
            const pos = params.get('tooltip') && params.get('tooltip').split(',')
            if (pos) this.showTooltip(pos)
          }, 1000)
          this.map.on('mouseenter', 'dataset-points', (e) => {
            this.map.getCanvas().style.cursor = 'pointer'
          })
          this.map.on('mouseleave', 'dataset-points', () => {
            this.map.getCanvas().style.cursor = ''
          })
        }
      })

      this.map.once('load', () => {
        if (this.config.tooltip && this.config.tooltip.length) {
          const params = new URLSearchParams(window.location.search)
          const pos = params.get('tooltip') && params.get('tooltip').split(',')
          if (pos) this.showTooltip(pos)
          this.map.on('mouseenter', 'dataset-points', (e) => {
            this.map.getCanvas().style.cursor = 'pointer'
          })
          this.map.on('mouseleave', 'dataset-points', () => {
            this.map.getCanvas().style.cursor = ''
          })
        }
      })
    },
    showTooltip (pos) {
      if (this.popup) this.popup.remove()
      const features = this.map.queryRenderedFeatures(this.map.project(pos), {
        layers: ['dataset-points']
      })
      if (this.config.tooltip && this.config.tooltip.length && features.length) {
        const params = new URLSearchParams(window.location.search)
        params.set('tooltip', pos.join(','))
        window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
        const qs = features.map(f => `${this.idProperty}:"${f.properties[this.idProperty]}"`).join(' OR ')
        const select = [].concat(this.config.tooltip)
        if (this.config.colors && this.config.colors.type === 'enum' && this.config.colors.enumOptions.field) select.push(this.config.colors.enumOptions.field)
        else if (this.config.colors && this.config.colors.type === 'range' && this.config.colors.rangeOptions.field) select.push(this.config.colors.rangeOptions.field)
        if (this.config.radius && this.config.radius.field) select.push(this.config.radius.field)
        axios.get(`${this.datasetUrl}/lines?size=1000&select=${select.join(',')}&qs=${encodeURIComponent(qs)}`).then(({ data }) => {
          const popupContent = new CPopup({
            propsData: { features: data.results },
            store: this.$store
          })
          popupContent.$vuetify = this.$vuetify
          popupContent.$mount()
          this.popup = new maplibregl.Popup({
            closeButton: false,
            offset: 4,
            maxWidth: Math.max(this.windowWidth / 3, 300)
          }).setLngLat(pos)
            .setDOMContent(popupContent.$el)
            .addTo(this.map)
          popupContent.$on('close', () => {
            this.popup.remove()
            popupContent.$destroy()
            const params = new URLSearchParams(window.location.search)
            params.delete('tooltip')
            window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
          })
        })
      }
    },
    bboxPasted () {
      const bbox = JSON.stringify([].concat(...this.map.getBounds().toArray()).map(n => n.toFixed(6)))
      navigator.clipboard.writeText(bbox)
      if (window.parent) window.parent.postMessage({ type: 'set-config', content: { field: 'bbox', value: bbox } }, '*')
    },
    params (p) {
      const params = new URLSearchParams(window.location.search)
      return params.get(p)
    },
    updatePointsLayer () {
      const pointsLayer = {
        id: 'dataset-points',
        source: 'data-fair',
        'source-layer': 'results',
        type: 'circle',
        minzoom: 2,
        paint: {
          'circle-stroke-width': ['interpolate', ['linear'], ['zoom'],
            2, ['case', ['boolean', ['feature-state', 'selected'], false], 2, 0.5],
            20, ['case', ['boolean', ['feature-state', 'selected'], false], 4, 1]
          ],
          'circle-stroke-color': ['case',
            ['boolean', ['feature-state', 'selected'], false],
            '#F00',
            '#000'

          ],
          'circle-stroke-opacity': 0.5,
          'circle-opacity': 0.7
        }
      }
      if (this.config.colors) {
        if (this.config.colors.type === 'single') {
          pointsLayer.paint['circle-color'] = this.config.colors.value
        } else if (this.config.colors.type === 'enum') {
          pointsLayer.paint['circle-color'] = [].concat('match', [['to-string', ['get', this.config.colors.enumOptions.field]]], ...colors(this.config.colors).map(c => [c.value, c.color]), this.config.colors.default)
        } else if (this.config.colors.type === 'range') {
          pointsLayer.paint['circle-color'] = ['case', ['has', this.config.colors.rangeOptions.field], [].concat('case', ...colors(this.config.colors, this.colorMetrics?.min, this.colorMetrics?.max, this.quantiles).map(c => [['all', ['>=', ['number', ['get', this.config.colors.rangeOptions.field]], c.min], ['<=', ['number', ['get', this.config.colors.rangeOptions.field]], c.max]], c.color]), this.config.colors.default), this.config.colors.default]
        }
      } else {
        pointsLayer.paint['circle-color'] = '#A4A4A4'
      }
      if (this.config.radius) {
        const vDiff = this.radiusMetrics.max - this.radiusMetrics.min
        if (this.config.radius.type === 'constant' || vDiff === 0) {
          pointsLayer.paint['circle-radius'] = { stops: [[2, this.config.radius.value], [11, this.config.radius.value * 2], [20, this.config.radius.value * 4]] }
        } else {
          const rDiff = this.config.radius.radiusMax - this.config.radius.radiusMin
          pointsLayer.paint['circle-radius'] = [
            'interpolate', ['linear'], ['zoom'],
            2, ['/', ['+', this.config.radius.radiusMin, ['/', ['*', ['-', ['coalesce', ['get', this.config.radius.field], this.radiusMetrics.avg], this.radiusMetrics.min], rDiff], vDiff]], 2],
            20, ['*', ['+', this.config.radius.radiusMin, ['/', ['*', ['-', ['coalesce', ['get', this.config.radius.field], this.radiusMetrics.avg], this.radiusMetrics.min], rDiff], vDiff]], 2]
          ]
        }
      }
      if (this.map.getLayer('dataset-points')) this.map.removeLayer('dataset-points')
      const layers = this.map.getStyle().layers.reverse()
      const labelsIdx = layers.findIndex(l => l.type !== 'symbol')
      const labelsLayerId = layers[labelsIdx - 1].id
      this.map.addLayer(pointsLayer, labelsLayerId)
    },
    toggleSatellite () {
      this.satellite = !this.satellite
      this.map.setLayoutProperty('satellite-tiles', 'visibility', this.satellite ? 'visible' : 'none')
    },
    initGeolocation () {
      this.geolocateControl = new maplibregl.GeolocateControl({
        showUserLocation: true,
        fitBoundsOptions: {
          maxZoom: 17,
          animate: false
        }
      })
      this.map.addControl(this.geolocateControl, 'top-right')

      this.geolocateControl.on('error', err => {
        console.log('geolocate error', err)
        this.geolocateControl.remove()
      })
    }
  }
}
</script>

<style lang="css">
</style>
